import React from 'react';
import CreateCaseDialog from './CreateCaseDialog';
import GlobalDocumentDrop from './GlobalDocumentDrop';

/**
 * "Create case" from anywhere in the staff portal (Lloyd, Oct 2026): the top-bar button, or a
 * document dropped or pasted anywhere. Mounted once, in ManagerLayout, so it exists on every staff
 * page and on none of the client portal or public intake pages.
 */
export interface CreateCaseStart {
  /** A document dropped, pasted or chosen. */
  file?: File;
  /** Or a document already on a client's file — read where it is, filed on the case as it is. */
  existingDocument?: { documentGuid: string; name: string };
  /** The case is for this client; no matching, no "new client". */
  lockedClient?: { userGuid: string; name: string };
}

interface CreateCaseContextValue {
  openCreateCase: (start?: CreateCaseStart) => void;
}

const CreateCaseContext = React.createContext<CreateCaseContextValue>({
  openCreateCase: () => undefined,
});

export const useCreateCase = (): CreateCaseContextValue => React.useContext(CreateCaseContext);

export const CreateCaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = React.useState(false);
  const [start, setStart] = React.useState<CreateCaseStart>({});
  const [ignoredFiles, setIgnoredFiles] = React.useState(0);
  // A fresh dialog each time, so nothing from an earlier document carries into the next.
  const [session, setSession] = React.useState(0);

  const begin = React.useCallback((next: CreateCaseStart, ignored: number) => {
    setStart(next);
    setIgnoredFiles(ignored);
    setSession((value) => value + 1);
    setOpen(true);
  }, []);

  const value = React.useMemo<CreateCaseContextValue>(
    () => ({ openCreateCase: (next?: CreateCaseStart) => begin(next ?? {}, 0) }),
    [begin],
  );
  const onDroppedFile = React.useCallback((file: File, ignored: number) => begin({ file }, ignored), [begin]);

  return (
    <CreateCaseContext.Provider value={value}>
      {children}
      <GlobalDocumentDrop dialogOpen={open} onFile={onDroppedFile} />
      {open && (
        <CreateCaseDialog
          key={session}
          start={start}
          ignoredFiles={ignoredFiles}
          onClose={() => setOpen(false)}
        />
      )}
    </CreateCaseContext.Provider>
  );
};
